import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Menu } from "lucide-react";
import ClientWorkspace from "./components/ClientWorkspace";
import ExpertWorkspace from "./components/ExpertWorkspace";
import PlatformWorkspace from "./components/PlatformWorkspace";
import WorkspaceSidebar from "./components/WorkspaceSidebar";
import { useTemplateCatalog, useTemplateModule, useTemplateModules } from "./hooks/useTemplateData";
import {
  clearFoundationAccessToken,
  getCurrentCompany,
  setFoundationAccessToken,
} from "./lib/api";
import { roleLabel } from "./lib/format";
import {
  FOUNDATION_SYSTEM_CODE,
  isTrustedFoundationMessage,
  resolveFoundationParentOrigin,
} from "./lib/foundationBridge";
import { PORTAL_ROLE } from "./lib/portal";
import { resolveCompanyRegion } from "./lib/regionFilter";
import {
  readTemplateLaunchSelection,
  resolveTemplateLaunchSelection,
} from "./lib/templateLaunchSelection";

// 搴旂敤澶栧３缁熶竴缂栨帓瀹㈡埛绔€佷笓瀹剁鍜屽钩鍙扮锛屽苟涓庤宓屽叆鐨勭郴缁熷簳搴у悓姝ュ竷灞€鐘舵€併€?
const defaultViews = {
  client: "library",
  expert: "review",
  platform: "overview",
};
const portalViews = {
  client: new Set(["library"]),
  expert: new Set(["review", "rules", "cases"]),
  platform: new Set(["overview", "portals", "assets", "intelligence", "tenants", "permissions"]),
};
const FOUNDATION_LAYOUT_MESSAGE = "foundation:shell-layout";
const FOUNDATION_BASE_LAYOUT_MESSAGE = "base:layout";
const FOUNDATION_AI_REGISTER_MESSAGE = "foundation:ai-register";
const FOUNDATION_BRIDGE_READY_MESSAGE = "foundation:bridge-ready";
const FOUNDATION_AI_OPEN_MESSAGE = "foundation:ai-open";
const FOUNDATION_AUTH_READY_MESSAGE = "foundation:auth-ready";
const FOUNDATION_AUTH_CONTEXT_MESSAGE = "foundation:auth-context";
const FOUNDATION_LOGOUT_MESSAGE = "foundation:logout";
const FOUNDATION_COMPANY_CHANGING_MESSAGE = "foundation:company-changing";
const FOUNDATION_SCENE = "templates";
const FOUNDATION_AUDIENCE = "templates-client";
const FOUNDATION_BRIDGE_PROTOCOL_VERSION = 2;
const FOUNDATION_BRIDGE_PROTOCOL_NAME = "foundation-relative-lifetime-v2";
const FOUNDATION_AUTH_REFRESH_LEAD_MS = 60_000;
const FOUNDATION_AUTH_MIN_REFRESH_MS = 5_000;
const DEFAULT_NAVIGATION_WIDTH = 224;
const MIN_NAVIGATION_WIDTH = 224;
const MAX_NAVIGATION_WIDTH = 420;
// AI 闈㈡澘榛樿/鏈€灏?鏈€澶у搴?
const DEFAULT_AI_WIDTH = 320;
const MIN_AI_WIDTH = 320;
const MAX_AI_WIDTH = 560;
const EDGE_HIDE_THRESHOLD = 16;
const PRODUCTION_CAT_AI_ORIGIN = "https://cartoon.ykq5580.com";

function isLocalBrowserHost() {
  return /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(window.location.hostname);
}

function localHttpOrigin(port) {
  // 仅本地调试时动态拼接本机端口，禁止把完整本机地址写进公网 JS 包。
  return `${window.location.protocol}//${["127", "0", "0", "1"].join(".")}:${port}`;
}

const CAT_AI_ORIGIN = isLocalBrowserHost()
  ? localHttpOrigin(8099)
  : PRODUCTION_CAT_AI_ORIGIN;
const CAT_AI_PANEL_URL = `${CAT_AI_ORIGIN}/cat-preview.html?embed=panel`;
const CAT_AI_FLOAT_URL = `${CAT_AI_ORIGIN}/cat-preview.html?embed=float`;
const CAT_AI_CHAT_URL = `${CAT_AI_ORIGIN}/api/ai/chat`;
const FOUNDATION_AUTH_WAIT_TIMEOUT_MS = 8000;
// 悬浮猫由统一卡通猫项目渲染，这里只给 iframe 预留完整画布，避免裁切猫脸和阴影。
const CAT_FLOAT_FRAME_WIDTH = 180;
const CAT_FLOAT_FRAME_HEIGHT = 180;

// 仅向底座登记文书模板业务能力；AI 外观和模型链路统一交给卡通猫项目。
const FOUNDATION_AI_PROFILE = {
  title: "亿企顺AI顾问",
  subtitle: "文书模板分系统：模板推荐、文书生成、字段补齐、条款风险",
  quickPrompts: ["推荐文书模板", "生成文书草稿", "检查缺失字段", "审查条款风险"],
  commonConsultations: [
    "帮我推荐适合的文书模板",
    "根据当前事项生成文书草稿",
    "这份模板还缺哪些字段？",
    "帮我检查文书条款风险",
    "生成一份可直接填写的材料清单",
  ],
  capabilitiesVersion: "templates-ai-20260730"
};

function foundationParentOrigin() {
  return resolveFoundationParentOrigin({
    portalRole: PORTAL_ROLE,
    search: window.location.search,
    referrer: document.referrer,
    isEmbedded: window.parent !== window,
  });
}

function readPortalView(role) {
  // URL 鍝堝笇浼樺厛鎸囧畾宸ヤ綔鍙帮紱鏃犳晥鍦板潃鍥為€€鍒板綋鍓嶈鑹茬殑榛樿棣栭〉銆?
  const parts = window.location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const requestedView = parts[0];
  return portalViews[role].has(requestedView) ? requestedView : defaultViews[role];
}

export default function App() {
  const parentOrigin = useMemo(foundationParentOrigin, []);
  // AI-BRIDGE-1.4锛氬綋鍓嶅垎绯荤粺闅愯棌鍙 AI 闈㈡澘锛屼粎淇濈暀搴曞骇韬唤鍜屽竷灞€妗ユ帴銆?
  const role = PORTAL_ROLE;
  const [roleView, setRoleView] = useState(() => readPortalView(role));
  const [clientDomain, setClientDomain] = useState("templates");
  const [selectedModuleKey, setSelectedModuleKey] = useState("recruitment");
  const [selectedSubmoduleKey, setSelectedSubmoduleKey] = useState("all");
  const [company, setCompany] = useState(null);
  const [serviceStatus, setServiceStatus] = useState("checking");
  const [navOpen, setNavOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(true);
  const [foundationLayout, setFoundationLayout] = useState({
    navigationVisible: true,
    aiVisible: true,
    navigationWidth: DEFAULT_NAVIGATION_WIDTH,
    assistantWidth: DEFAULT_AI_WIDTH,
  });
  const [catFloatPos, setCatFloatPos] = useState(() => ({
    x: Math.max(16, window.innerWidth - CAT_FLOAT_FRAME_WIDTH - 16),
    y: Math.max(86, window.innerHeight - CAT_FLOAT_FRAME_HEIGHT - 24),
  }));
  // 缁熶竴鐚舰璞′紭鍏堜娇鐢ㄥ垎绯荤粺鍐呯疆璧勬簮锛屽簳搴т笅鍙戞椂鍔ㄦ€佹浛鎹€?
  const [foundationContext, setFoundationContext] = useState(null);
  const [foundationAuthTimedOut, setFoundationAuthTimedOut] = useState(false);
  const [aiContext, setAiContext] = useState({
    page: "鏂囦功妯℃澘",
    moduleKey: "recruitment",
    moduleName: "鎷涜仒绠＄悊",
    currentProject: "",
    templateName: "",
    templateId: "",
  });
  const [externalAiDraft, setExternalAiDraft] = useState(null);
  const [catFrameLoadTick, setCatFrameLoadTick] = useState(0);
  const mainWorkspaceRef = useRef(null);
  const catPanelFrameRef = useRef(null);
  const catFloatFrameRef = useRef(null);
  const launchSelectionRef = useRef(readTemplateLaunchSelection());
  const launchSelectionAppliedRef = useRef(false);

  const {
    catalog,
    status: catalogStatus,
    error: catalogError,
    retry: retryCatalog,
  } = useTemplateCatalog();
  const overviewModuleKeys = useMemo(
    () => catalog?.modules.map((module) => module.moduleKey) || [],
    [catalog],
  );
  const { modulesByKey: overviewModulesByKey, status: overviewModulesStatus } =
    useTemplateModules(overviewModuleKeys);
  const { moduleData, status: moduleStatus } = useTemplateModule(selectedModuleKey);
  const companyRegion = useMemo(
    () => resolveCompanyRegion(company, foundationContext),
    [company, foundationContext],
  );

  useEffect(() => {
    if (!catalog || launchSelectionAppliedRef.current) return;
    const matchedSelection = resolveTemplateLaunchSelection(catalog, launchSelectionRef.current);
    if (!matchedSelection) return;
    launchSelectionAppliedRef.current = true;
    setClientDomain(matchedSelection.domain);
    setSelectedModuleKey(matchedSelection.moduleKey);
    setSelectedSubmoduleKey(matchedSelection.submoduleKey);
  }, [catalog]);

  useEffect(() => {
    function syncPortalView() {
      const nextView = readPortalView(role);
      setRoleView(nextView);
      const expectedHash = "#/" + nextView;
      if (window.location.hash !== expectedHash) {
        window.history.replaceState(null, "", expectedHash);
      }
    }
    syncPortalView();
    window.addEventListener("hashchange", syncPortalView);
    return () => window.removeEventListener("hashchange", syncPortalView);
  }, [role]);

  useEffect(() => {
    document.title = "鏂囦功鏅鸿兘" + roleLabel(role);
  }, [role]);

  useEffect(() => {
    if (!parentOrigin) return undefined;
    let nonce = "";
    let refreshTimer = 0;
    let authWaitTimer = 0;
    const postToFoundation = (message) => window.parent.postMessage(message, parentOrigin);
    const registerAiProfile = () => {
      // 鍙悓姝ュ睍绀洪厤缃紝妯℃澘鎺ㄨ崘/鑽夌/瀛楁妫€鏌?鏉℃椋庨櫓浠嶇敱鏈垎绯荤粺涓氬姟 AI 璐熻矗銆?
      postToFoundation({
        type: FOUNDATION_AI_REGISTER_MESSAGE,
        version: 1,
        systemCode: FOUNDATION_SYSTEM_CODE,
        scene: FOUNDATION_SCENE,
        // 文书模板 AI 归分系统自己渲染，底座只做桥接同步，避免恢复底座 AI 兜底。
        aiOwnership: "subsystem",
        uiMode: "subsystem-rendered",
        layoutOwnership: "subsystem",
        protocolVersion: "AI-BRIDGE-1.4",
        ...FOUNDATION_AI_PROFILE,
      });
    };
    const requestAuthContext = () => {
      // 姣忔璇锋眰鐢熸垚鏂?nonce锛屽彧鎺ュ彈涓庢湰杞姹傚尮閰嶇殑璁よ瘉涓婁笅鏂囷紝閬垮厤鏃ф秷鎭閲嶅浣跨敤銆?
      nonce = window.crypto?.randomUUID?.()
        || `${FOUNDATION_SYSTEM_CODE}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setFoundationAuthTimedOut(false);
      window.clearTimeout(authWaitTimer);
      authWaitTimer = window.setTimeout(() => {
        // 桥接身份长时间未下发时只展示可重试提示，禁止降级挂载受保护页面并发送无令牌请求。
        setFoundationAuthTimedOut(true);
      }, FOUNDATION_AUTH_WAIT_TIMEOUT_MS);
      postToFoundation({
        type: FOUNDATION_AUTH_READY_MESSAGE,
        protocolVersion: FOUNDATION_BRIDGE_PROTOCOL_VERSION,
        foundationBridgeProtocol: FOUNDATION_BRIDGE_PROTOCOL_NAME,
        systemCode: FOUNDATION_SYSTEM_CODE,
        serviceCode: FOUNDATION_SYSTEM_CODE,
        portalType: "client",
        endpointType: "frontend",
        audience: FOUNDATION_AUDIENCE,
        nonce,
      });
    };
    function handleFoundationMessage(event) {
      // postMessage 蹇呴』鍚屾椂鏍￠獙绐楀彛瀵硅薄銆佹潵婧愬拰鍗忚鐗堟湰锛屽啀澶勭悊浠讳綍甯冨眬鎴栬璇佹暟鎹€?
      if (!isTrustedFoundationMessage(event, window.parent, parentOrigin)) return;
      const isAuthContextMessage = event.data?.type === FOUNDATION_AUTH_CONTEXT_MESSAGE;
      if (!isAuthContextMessage && event.data?.version !== 1) return;
      if (
        (event.data.type === FOUNDATION_LAYOUT_MESSAGE || event.data.type === FOUNDATION_BASE_LAYOUT_MESSAGE)
        && event.data.systemCode === FOUNDATION_SYSTEM_CODE
      ) {
        // 兼容底座按钮历史字段：右上角“显示/隐藏 AI”可能下发 aiVisible，也可能下发 aiPanelVisible；
        // 宽度可能是 assistantWidth、aiWidth 或 aiPanelWidth。分系统自己消化协议差异，底座不再为单系统加特判。
        const rawAiVisible = event.data.aiVisible ?? event.data.aiPanelVisible;
        const rawNavigationVisible = event.data.navigationVisible ?? event.data.navVisible;
        const widthValue = event.data.assistantWidth ?? event.data.aiWidth ?? event.data.aiPanelWidth;
        const nextAiVisible = rawAiVisible !== false;
        const nextNavigationVisible = rawNavigationVisible !== false;
        setFoundationLayout((current) => ({
          navigationVisible: nextNavigationVisible,
          aiVisible: nextAiVisible,
          navigationWidth: Math.min(
            MAX_NAVIGATION_WIDTH,
            Math.max(MIN_NAVIGATION_WIDTH, Number(event.data.navigationWidth) || current.navigationWidth || DEFAULT_NAVIGATION_WIDTH),
          ),
          assistantWidth: Math.min(
            MAX_AI_WIDTH,
            Math.max(MIN_AI_WIDTH, Number(widthValue) || current.assistantWidth || DEFAULT_AI_WIDTH),
          ),
        }));
        if (nextAiVisible) {
          setAiOpen(true);
        } else {
          setAiOpen(false);
        }
        if (!nextNavigationVisible) {
          setNavOpen(false);
        }
        return;
      }
      if (event.data.type === FOUNDATION_LOGOUT_MESSAGE || event.data.type === FOUNDATION_COMPANY_CHANGING_MESSAGE) {
        window.clearTimeout(refreshTimer);
        clearFoundationAccessToken();
        setFoundationContext(null);
        setCompany(null);
        setServiceStatus("checking");
        return;
      }
      if (
        event.data.type === FOUNDATION_AI_OPEN_MESSAGE
        || event.data.type === "cat:ai-open"
        || event.data.type === "foundation-cat:open-panel"
      ) {
        openAi(event.data.prompt || "");
        return;
      }
      // 璁よ瘉涓婁笅鏂囪繕闇€缁戝畾绯荤粺銆佺鐐广€佸彈浼椼€乶once 鍜屼笟鍔℃潈闄愶紝浠讳竴涓嶇閮界洿鎺ュ拷鐣ャ€?
      if (
        event.data.type !== FOUNDATION_AUTH_CONTEXT_MESSAGE
        || event.data.systemCode !== FOUNDATION_SYSTEM_CODE
        || event.data.serviceCode !== FOUNDATION_SYSTEM_CODE
        || event.data.portalType !== "client"
        || event.data.endpointType !== "frontend"
        || event.data.audience !== FOUNDATION_AUDIENCE
        || event.data.nonce !== nonce
        || !event.data.accessToken
        || !event.data.permissions?.includes("module:templates:access")
      ) return;
      const expiresInSeconds = Number(event.data.expiresInSeconds);
      // 中文维护注释：认证只接受相对寿命协议 v2，旧版本或绝对过期字段出现时直接拒绝。
      if (
        event.data.protocolVersion !== FOUNDATION_BRIDGE_PROTOCOL_VERSION
        || event.data.foundationBridgeProtocol !== FOUNDATION_BRIDGE_PROTOCOL_NAME
        || Object.prototype.hasOwnProperty.call(event.data, "version")
        || Object.prototype.hasOwnProperty.call(event.data, "expiresAt")
        || !Number.isFinite(expiresInSeconds)
        || expiresInSeconds <= 0
      ) return;
      window.clearTimeout(authWaitTimer);
      setFoundationAuthTimedOut(false);
      setFoundationAccessToken(event.data.accessToken);
      setFoundationContext(event.data);
      // 企业切换或认证续期使用本次快照，不能让旧企业字段覆盖当前地区和行业。
      setCompany({
        ...(event.data.tenant || {}),
        ...(event.data.company || {}),
        ...(event.data.companyProfile || {}),
      });
      window.clearTimeout(refreshTimer);
      const deadlineMonotonicMs = performance.now() + expiresInSeconds * 1000;
      refreshTimer = window.setTimeout(
        requestAuthContext,
        Math.max(
          FOUNDATION_AUTH_MIN_REFRESH_MS,
          deadlineMonotonicMs - performance.now() - FOUNDATION_AUTH_REFRESH_LEAD_MS,
        ),
      );
    }

    window.addEventListener("message", handleFoundationMessage);
    window.addEventListener("focus", requestAuthContext);
    postToFoundation({
      type: FOUNDATION_BRIDGE_READY_MESSAGE,
      version: 1,
      systemCode: FOUNDATION_SYSTEM_CODE,
      scene: FOUNDATION_SCENE,
      protocolVersion: "AI-BRIDGE-1.4",
      // 文书模板自己渲染右侧标准卡通猫 iframe，底座只负责身份、权限和布局同步。
      hasOwnAi: true,
      hasOwnCatAvatar: true,
      aiOwnership: "subsystem",
      uiMode: "subsystem-rendered",
      layoutOwnership: "subsystem",
      preferredNavWidth: DEFAULT_NAVIGATION_WIDTH,
      preferredAssistantWidth: DEFAULT_AI_WIDTH,
      capabilities: ["navigation", "auth", "company-sync", "logout", "layout-request"],
    });
    registerAiProfile();
    requestAuthContext();
    return () => {
      window.clearTimeout(refreshTimer);
      window.clearTimeout(authWaitTimer);
      clearFoundationAccessToken();
      window.removeEventListener("message", handleFoundationMessage);
      window.removeEventListener("focus", requestAuthContext);
    };
  }, [parentOrigin]);

  useEffect(() => {
    // 宓屽叆妯″紡鍏堢瓑寰呭簳搴т笅鍙戣闂护鐗岋紝鍐嶈姹備紒涓氫俊鎭紝閬垮厤鍙戝嚭鏈璇佺殑涓氬姟璇锋眰銆?
    if (parentOrigin && !foundationContext) return undefined;
    if (parentOrigin && foundationContext) {
      setServiceStatus("online");
      return undefined;
    }
    if (!parentOrigin) {
      setServiceStatus("offline");
      return undefined;
    }
    const controller = new AbortController();
    setServiceStatus("checking");
    getCurrentCompany(controller.signal)
      .then((payload) => {
        setCompany((current) => ({ ...(current || {}), ...(payload || {}) }));
        setServiceStatus("online");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setServiceStatus("offline");
      });
    return () => controller.abort();
  }, [foundationContext, parentOrigin]);

  useEffect(() => {
    if (!moduleData || moduleData.moduleKey !== selectedModuleKey) return;
    const submoduleExists =
      selectedSubmoduleKey === "all" ||
      moduleData.submodules.some((item) => item.submoduleKey === selectedSubmoduleKey);
    if (!submoduleExists) setSelectedSubmoduleKey("all");
  }, [moduleData, selectedModuleKey, selectedSubmoduleKey]);

  useEffect(() => {
    if (mainWorkspaceRef.current) {
      mainWorkspaceRef.current.scrollTop = 0;
    }
  }, [clientDomain, role, roleView, selectedModuleKey]);

  const selectedModuleSummary = catalog?.modules.find(
    (module) => module.moduleKey === selectedModuleKey,
  );

  const stableContextChange = useCallback((nextContext) => {
    setAiContext((current) => {
      const next = {
        ...current,
        ...nextContext,
        moduleKey: nextContext.moduleKey || current.moduleKey,
        moduleName: nextContext.moduleName || current.moduleName,
      };
      const unchanged = Object.keys(next).every((key) => next[key] === current[key]);
      return unchanged ? current : next;
    });
  }, []);

  useEffect(() => {
    stableContextChange({
      moduleKey: selectedModuleKey,
      moduleName: selectedModuleSummary?.moduleName || moduleData?.moduleName || "",
    });
  }, [
    moduleData?.moduleName,
    selectedModuleKey,
    selectedModuleSummary?.moduleName,
    stableContextChange,
  ]);

  function changeRoleView(nextView) {
    const safeView = portalViews[role].has(nextView) ? nextView : defaultViews[role];
    setRoleView(safeView);
    window.location.hash = "#/" + safeView;
  }

  function changeClientDomain(nextDomain) {
    setClientDomain(nextDomain);
    setSelectedSubmoduleKey("all");
    if (nextDomain === "risk" && selectedModuleKey === "commercial-exchange") {
      setSelectedModuleKey("recruitment");
    }
    if (nextDomain === "commercial") {
      setSelectedModuleKey("commercial-exchange");
    }
  }

  function changeModule(moduleKey, nextDomain) {
    setSelectedModuleKey(moduleKey);
    setSelectedSubmoduleKey("all");
    if (nextDomain) setClientDomain(nextDomain);
  }

  const openAi = useCallback((prompt = "") => {
    const safePrompt = String(prompt || "").slice(0, 2000);
    if (parentOrigin) {
      // 搴曞骇鍙礋璐ｆ墦寮€缁熶竴闈㈡澘骞堕濉彁绀鸿瘝锛屼笉鐩存帴鎵ц鏂囦功鐢熸垚绛変笟鍔″姩浣溿€?
      window.parent.postMessage({
        type: FOUNDATION_AI_OPEN_MESSAGE,
        version: 1,
        systemCode: FOUNDATION_SYSTEM_CODE,
        scene: FOUNDATION_SCENE,
        aiVisible: true,
      }, parentOrigin);
    }
    setFoundationLayout((current) => ({ ...current, aiVisible: true }));
    if (safePrompt) setExternalAiDraft({ id: Date.now(), text: safePrompt });
    setAiOpen(true);
  }, [parentOrigin]);

  const minimizeAi = useCallback(() => {
    setAiOpen(false);
    setFoundationLayout((current) => ({ ...current, aiVisible: false }));
    if (parentOrigin) {
      window.parent.postMessage({
        type: "foundation:shell-layout-request",
        version: 1,
        systemCode: FOUNDATION_SYSTEM_CODE,
        scene: FOUNDATION_SCENE,
        aiVisible: false,
      }, parentOrigin);
    }
  }, [parentOrigin]);

  useEffect(() => {
    if (window.yiqishunFoundationBridge) return undefined;
    Object.defineProperty(window, "yiqishunFoundationBridge", {
      configurable: false,
      enumerable: false,
      writable: false,
      value: {
        isActive: () => window.parent !== window,
        openAiPanel: () => openAi(),
        minimizeAiPanel: () => minimizeAi(),
      },
    });
    return undefined;
  }, [openAi, minimizeAi]);

  function askAi(text) {
    openAi(text);
  }

  const restoreAiPanel = useCallback(() => {
    openAi();
  }, [openAi]);

  const aiPanelVisible = foundationLayout.aiVisible && aiOpen;
  const assistantBusinessContext = useMemo(() => ({
    type: "foundation-cat:business-context",
    version: 1,
    systemCode: FOUNDATION_SYSTEM_CODE,
    scene: FOUNDATION_SCENE,
    chatEndpoint: CAT_AI_CHAT_URL,
    portalRole: role,
    portalName: roleLabel(role),
    currentPage: aiContext.page,
    currentModule: aiContext.moduleName,
    currentModuleKey: aiContext.moduleKey,
    currentProject: aiContext.currentProject,
    currentTemplateName: aiContext.templateName,
    currentTemplateId: aiContext.templateId,
    companyId: company?.id || foundationContext?.company?.id || foundationContext?.tenant?.id || "",
    companyName: company?.name || foundationContext?.company?.name || foundationContext?.tenant?.name || "",
    companyRegion,
    serviceStatus,
    permissions: foundationContext?.permissions || [],
    executableActions: ["recommend-template", "generate-draft", "check-fields", "review-clause-risk"],
    requiresConfirmationActions: ["submit", "approve", "pay", "sign", "delete", "import", "export", "declare", "confirm"],
    memoryPolicy: "templates-web4",
    optimizationPolicy: "feedback-driven",
    draft: externalAiDraft?.text || "",
  }), [aiContext, company, companyRegion, externalAiDraft, foundationContext, role, serviceStatus]);

  useEffect(() => {
    const targetFrame = aiPanelVisible ? catPanelFrameRef.current : catFloatFrameRef.current;
    // 统一猫 iframe 真正加载后再注入业务上下文，避免 about:blank 阶段 postMessage 产生目标域警告。
    if (targetFrame?.dataset.catLoaded === "1" && targetFrame?.src?.startsWith(`${CAT_AI_ORIGIN}/`)) {
      targetFrame.contentWindow?.postMessage(assistantBusinessContext, CAT_AI_ORIGIN);
    }
  }, [aiPanelVisible, assistantBusinessContext, catFrameLoadTick]);

  const markCatFrameLoaded = useCallback((event) => {
    const frame = event.currentTarget;
    try {
      const frameHref = frame.contentWindow?.location?.href || "";
      if (!frameHref.startsWith(`${CAT_AI_ORIGIN}/`)) return;
    } catch {
      // 成功跨域加载统一猫页面后，读取 location 会被浏览器拦截，此时可以安全按目标域 postMessage。
    }
    frame.dataset.catLoaded = "1";
    setCatFrameLoadTick((value) => value + 1);
  }, []);

  useEffect(() => {
    function handleCatMessage(event) {
      if (event.origin !== CAT_AI_ORIGIN || event.data?.version !== 1) return;
      if (
        event.data.type === FOUNDATION_AI_OPEN_MESSAGE
        || event.data.type === "cat:ai-open"
        || event.data.type === "foundation-cat:open-panel"
      ) {
        openAi(event.data.prompt || "");
      }
    }
    window.addEventListener("message", handleCatMessage);
    return () => window.removeEventListener("message", handleCatMessage);
  }, [openAi]);

  const startSidebarResize = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = foundationLayout.navigationWidth;
    document.body.classList.add("sidebar-resizing");

    function handlePointerMove(moveEvent) {
      const hidden = moveEvent.clientX <= EDGE_HIDE_THRESHOLD;
      const nextWidth = Math.min(
        MAX_NAVIGATION_WIDTH,
        Math.max(MIN_NAVIGATION_WIDTH, startWidth + moveEvent.clientX - startX),
      );
      setFoundationLayout((current) => ({
        ...current,
        navigationVisible: !hidden,
        navigationWidth: hidden ? DEFAULT_NAVIGATION_WIDTH : nextWidth,
      }));
      if (hidden) {
        setNavOpen(false);
      }
    }

    function handlePointerUp() {
      document.body.classList.remove("sidebar-resizing");
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
  }, [foundationLayout.navigationWidth]);

  // 鎷栧姩鍙充晶鍒嗙晫绾匡紝鍔ㄦ€佽皟鏁?AI 闈㈡澘瀹藉害锛涙嫋鍒板彸渚ч槇鍊煎唴鏃堕殣钘?
  const startAssistantResize = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = foundationLayout.assistantWidth;
    document.body.classList.add("assistant-resizing");

    function handlePointerMove(moveEvent) {
      const hidden = window.innerWidth - moveEvent.clientX <= EDGE_HIDE_THRESHOLD;
      const nextWidth = Math.min(
        MAX_AI_WIDTH,
        Math.max(MIN_AI_WIDTH, startWidth - (moveEvent.clientX - startX)),
      );
      setFoundationLayout((current) => ({
        ...current,
        aiVisible: !hidden,
        assistantWidth: hidden ? MIN_AI_WIDTH : nextWidth,
      }));
      if (hidden) {
        setAiOpen(false);
      }
    }

    function handlePointerUp() {
      document.body.classList.remove("assistant-resizing");
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
  }, [foundationLayout.assistantWidth]);

  // 鍙屽嚮宸︿晶鍒嗙晫绾挎仮澶嶅乏渚у鑸粯璁ゅ搴?
  function resetSidebarWidth() {
    setFoundationLayout((current) => ({
      ...current,
      navigationVisible: true,
      navigationWidth: DEFAULT_NAVIGATION_WIDTH,
    }));
  }

  // 鍙屽嚮鍙充晶鍒嗙晫绾挎仮澶?AI 闈㈡澘榛樿瀹藉害
  function resetAssistantWidth() {
    setFoundationLayout((current) => ({
      ...current,
      aiVisible: true,
      assistantWidth: DEFAULT_AI_WIDTH,
    }));
    setAiOpen(true);
  }

  const startCatFloatDrag = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startPos = { ...catFloatPos };

    function handleMove(moveEvent) {
      setCatFloatPos({
        x: Math.max(8, Math.min(window.innerWidth - CAT_FLOAT_FRAME_WIDTH - 8, startPos.x + moveEvent.clientX - startX)),
        y: Math.max(8, Math.min(window.innerHeight - CAT_FLOAT_FRAME_HEIGHT - 8, startPos.y + moveEvent.clientY - startY)),
      });
    }

    function handleUp() {
      document.body.classList.remove("cat-float-dragging");
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    }

    document.body.classList.add("cat-float-dragging");
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }, [catFloatPos]);

  // 娴姩鐚敮鎸佹嫋鎷藉畾浣嶏紝閬垮厤閬尅涓诲唴瀹?
  const mainContent =
    role === "client" ? (
      <ClientWorkspace
        catalog={catalog}
        catalogStatus={catalogStatus}
        catalogError={catalogError}
        onCatalogRetry={retryCatalog}
        domain={clientDomain}
        onDomainChange={setClientDomain}
        selectedModuleKey={selectedModuleKey}
        onModuleChange={changeModule}
        selectedSubmoduleKey={selectedSubmoduleKey}
        onSubmoduleChange={setSelectedSubmoduleKey}
        moduleData={moduleData}
        moduleStatus={moduleStatus}
        overviewModulesByKey={overviewModulesByKey}
        overviewModulesStatus={overviewModulesStatus}
        company={company}
        companyRegion={companyRegion}
        onAskAi={askAi}
        onContextChange={stableContextChange}
      />
    ) : role === "expert" ? (
      <ExpertWorkspace
        view={roleView}
        catalog={catalog}
        catalogStatus={catalogStatus}
        catalogError={catalogError}
        onCatalogRetry={retryCatalog}
        moduleData={moduleData}
        moduleStatus={moduleStatus}
        selectedModuleKey={selectedModuleKey}
        companyRegion={companyRegion}
        onModuleChange={(moduleKey) => changeModule(moduleKey)}
        onAskAi={askAi}
        onContextChange={stableContextChange}
      />
    ) : (
      <PlatformWorkspace
        view={roleView}
        catalog={catalog}
        catalogStatus={catalogStatus}
        catalogError={catalogError}
        onCatalogRetry={retryCatalog}
        company={company}
        companyRegion={companyRegion}
        moduleData={moduleData}
        moduleStatus={moduleStatus}
        serviceStatus={serviceStatus}
        onContextChange={stableContextChange}
      />
    );

  if (parentOrigin && !foundationContext) {
    return (
      <main
        className="foundation-auth-pending"
        aria-label={foundationAuthTimedOut ? "底座身份同步超时" : "正在同步底座身份"}
        aria-busy={!foundationAuthTimedOut}
      >
        {foundationAuthTimedOut ? (
          <section className="foundation-auth-timeout" role="alert">
            <strong>底座身份同步超时</strong>
            <span>请重新同步身份后再进入文书模板，系统不会以未授权状态加载业务数据。</span>
            <button type="button" className="primary-button" onClick={() => window.location.reload()}>
              重新同步
            </button>
          </section>
        ) : null}
      </main>
    );
  }

  return (
    <div
      className={`application ${window.parent !== window ? "foundation-embedded" : ""} ${!foundationLayout.navigationVisible ? "foundation-navigation-hidden" : ""} ${!aiPanelVisible ? "foundation-ai-hidden" : ""}`}
      style={{
        "--sidebar-width": `${foundationLayout.navigationWidth}px`,
        "--assistant-width": `${foundationLayout.assistantWidth}px`,
      }}
    >
      <div className="mobile-topbar">
        <button className="icon-button" type="button" onClick={() => setNavOpen(true)} aria-label="鎵撳紑瀵艰埅">
          <Menu size={19} />
        </button>
        <div>
          <strong>鏂囦功鏅鸿兘</strong>
          <span>{roleLabel(role)}</span>
        </div>
      </div>

      <div className="application-grid">
        <WorkspaceSidebar
          role={role}
          roleView={roleView}
          onRoleViewChange={changeRoleView}
          catalog={catalog}
          catalogStatus={catalogStatus}
          serviceStatus={serviceStatus}
          clientDomain={clientDomain}
          selectedModuleKey={selectedModuleKey}
          selectedSubmoduleKey={selectedSubmoduleKey}
          onClientDomainChange={changeClientDomain}
          onModuleChange={changeModule}
          onSubmoduleChange={setSelectedSubmoduleKey}
          moduleData={moduleData}
          overviewModulesByKey={overviewModulesByKey}
          companyRegion={companyRegion}
          company={company}
          open={navOpen}
          onClose={() => setNavOpen(false)}
        />
        <button
          className="sidebar-resize-handle"
          type="button"
          onPointerDown={startSidebarResize}
          onMouseDown={startSidebarResize}
          onDoubleClick={resetSidebarWidth}
          aria-label="拖动调整左侧导航栏宽度"
          title="拖动调整左侧导航栏宽度"
        />
        {aiPanelVisible ? (
          <button
            className="assistant-resize-handle"
            type="button"
            onPointerDown={startAssistantResize}
            onMouseDown={startAssistantResize}
            onDoubleClick={resetAssistantWidth}
            aria-label="鎷栧姩璋冩暣 AI 闈㈡澘瀹藉害"
            title="鎷栧姩璋冩暣 AI 闈㈡澘瀹藉害"
          />
        ) : null}

        {navOpen ? <button className="drawer-scrim nav-scrim" type="button" onClick={() => setNavOpen(false)} aria-label="鍏抽棴瀵艰埅" /> : null}

        <main className="main-workspace" ref={mainWorkspaceRef}>
          {mainContent}
        </main>

        {aiPanelVisible ? (
          <aside className="foundation-cat-ai-panel" aria-label="亿企顺AI顾问">
            <iframe
              ref={catPanelFrameRef}
              className="foundation-cat-ai-frame"
              src={CAT_AI_PANEL_URL}
              title="亿企顺AI顾问"
              onLoad={markCatFrameLoaded}
            />
          </aside>
        ) : (
          <div
            className="foundation-cat-ai-float-host"
            style={{ left: `${catFloatPos.x}px`, top: `${catFloatPos.y}px` }}
            onDoubleClick={restoreAiPanel}
            onPointerDown={startCatFloatDrag}
            onMouseDown={startCatFloatDrag}
          >
            <iframe
              ref={catFloatFrameRef}
              className="foundation-cat-ai-float-frame"
              src={CAT_AI_FLOAT_URL}
              title="亿企顺AI"
              onLoad={markCatFrameLoaded}
            />
          </div>
        )}
      </div>
    </div>
  );
}
